import type { PlainButtonProps } from '@customerio/pluma-components/react';
import { Icon, PlainButton, Spinner, Tooltip, VisuallyHidden } from '@customerio/pluma-components/react';
import type { IconName } from '@customerio/pluma-icons';
import clsx from 'clsx';
import { forwardRef } from 'react';
import * as styles from './index.css';
import { isEmptyString } from '@/utils/string';
import { mergeRefs } from 'react-merge-refs';

/**
 * Icon Button component for the top navigation actions.
 */
export const IconButton = forwardRef<
  HTMLButtonElement,
  {
    icon: IconName;
    label: string;
    tooltip?: string;
    isLoading?: boolean;
    children?: React.ReactNode;
    className?: string;
    showLabel?: boolean;
  } & PlainButtonProps
>(function IconButton({ icon, label, tooltip, isLoading, children, className, showLabel = false, ...rest }, ref) {
  return (
    <Tooltip content={tooltip} isDisabled={isEmptyString(tooltip)} isCustom={true}>
      {(context) => (
        <PlainButton
          ref={mergeRefs([ref, context.reference.setReference])}
          className={clsx(styles.iconButton, showLabel && styles.iconButtonWithLabel, className)}
          {...context.reference.getReferenceProps(rest as any)}
        >
          {showLabel ? null : <VisuallyHidden>{label}</VisuallyHidden>}

          <div className={styles.iconButtonIcon}>
            {isLoading === true ? <Spinner color="invert" /> : <Icon name={icon} size="md" sizeVersion="v2" />}
          </div>
          {showLabel ? <span className={styles.iconButtonLabel}>{label}</span> : null}
          {children}
        </PlainButton>
      )}
    </Tooltip>
  );
});
